import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { dashApi } from "@/lib/dashApi";
import { fmtCurrency, fmtDateTime, fmtNumber, fmtRelative } from "@/lib/format";
import { EmptyState, ErrorState, LoadingState, PageHeader, Panel, SourceBadge, StatusPill, Stat } from "@/components/dash/ui";

const MATCH_PILL: Record<string, string> = { confirmed: "ok", auto: "info", unmatched: "warning", rejected: "down" };

export default function CustomerJourneyPage() {
  const { id = "" } = useParams();
  const q = useQuery({ queryKey: ["customer", id], queryFn: () => dashApi.customerDetail(id), enabled: !!id });
  const c = q.data;

  return (
    <div className="space-y-6">
      <PageHeader
        title={c?.name ?? "Customer"}
        description={c?.email ?? "Cross-system customer profile and activity journey."}
        right={<Link to="/customers" className="text-xs text-slate-400 hover:text-brand-300">← All customers</Link>}
      />
      {q.isLoading ? <LoadingState /> : q.isError ? <ErrorState error={q.error} /> : c && (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Stat label="Total spend" value={fmtCurrency(c.total_spend)} sub="Odoo + ecommerce + subscriptions" />
            <Stat label="Estimated LTV" value={fmtCurrency(c.ltv_estimate)} />
            <Stat label="Repairs" value={fmtNumber(c.repair_count)} sub={`${fmtCurrency(c.repair_value)} value`} />
            <Stat label="Services used" value={c.services_used} sub={`${c.open_issues} open issue(s)`} />
            <Stat label="Ecommerce orders" value={fmtNumber(c.order_count)} />
            <Stat label="Odoo invoices" value={fmtNumber(c.invoice_count)} />
            <Stat label="Subscriptions" value={fmtNumber(c.subscription_count)} />
            <Stat label="Support contacts" value={fmtNumber(c.support_count)} />
          </div>
          <div className="flex flex-wrap gap-4 text-xs text-slate-500">
            <span>First seen: {fmtDateTime(c.first_seen)}</span>
            <span>Last activity: {fmtRelative(c.last_activity)}</span>
          </div>
          <Panel title="Activity by system" subtitle="Each linked source contributes to this customer's unified profile.">
            {!c.sources.length ? <EmptyState compact>No linked sources.</EmptyState> : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500">
                    <th className="pb-2 font-medium">System</th>
                    <th className="pb-2 font-medium">As known there</th>
                    <th className="pb-2 text-right font-medium">Spend</th>
                    <th className="pb-2 text-right font-medium">Txns</th>
                    <th className="pb-2 text-right font-medium">Open</th>
                    <th className="pb-2 text-right font-medium">Last seen</th>
                    <th className="pb-2 text-right font-medium">Match</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-ink-500/40">
                  {c.sources.map((s) => (
                    <tr key={`${s.source}-${s.source_id}`} className="text-slate-300">
                      <td className="py-2"><SourceBadge code={s.source} /></td>
                      <td className="py-2">
                        <span className="text-slate-200">{s.name || "—"}</span>
                        <div className="text-[11px] text-slate-600">{s.email ?? ""}</div>
                      </td>
                      <td className="py-2 text-right tabular-nums text-white">{s.spend ? fmtCurrency(s.spend) : "—"}</td>
                      <td className="py-2 text-right tabular-nums">{fmtNumber(s.txn_count)}</td>
                      <td className={`py-2 text-right tabular-nums ${s.open_count ? "text-amber-300" : "text-slate-500"}`}>{s.open_count}</td>
                      <td className="py-2 text-right text-[11px] text-slate-500">{fmtRelative(s.last_activity)}</td>
                      <td className="py-2 text-right">
                        <StatusPill status={MATCH_PILL[s.match_status] ?? "info"} label={`${s.match_status} ${Math.round(s.match_confidence * 100)}%`} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Panel>
        </>
      )}
    </div>
  );
}
